/**
 * Workspace Notifications Hook
 *
 * Fetches Central-backed workspace notifications via React Query.
 * Used by NotificationPanel's "Workspace" tab.
 *
 * App-local: wiring, polling interval, and error handling are app-owned.
 * Shared: types and REST client come from @levelup/central-notifications.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { CentralNotification } from '@levelup/central-notifications';
import type { RootState } from '../store';
import {
  fetchWorkspaceInbox,
  markNotificationRead,
} from '../lib/centralNotifications';

interface UseWorkspaceNotificationsOptions {
  enabled?: boolean;
}

interface UseWorkspaceNotificationsResult {
  notifications: CentralNotification[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  markRead: (notificationId: string) => void;
}

export function useWorkspaceNotifications(
  options: UseWorkspaceNotificationsOptions = {},
): UseWorkspaceNotificationsResult {
  const workspaceId = useSelector(
    (state: RootState) => state.boot.context?.currentWorkspace?.id ?? null,
  );

  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['workspaceNotifications', workspaceId],
    queryFn: () => fetchWorkspaceInbox({ workspaceId: workspaceId! }),
    enabled: !!workspaceId && (options.enabled ?? true),
    refetchInterval: 60000,
    staleTime: 30000,
    retry: 1,
  });

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workspaceNotifications', workspaceId],
      });
      queryClient.invalidateQueries({ queryKey: ['workspaceUnreadCount'] });
    },
  });

  return {
    notifications: data?.notifications ?? [],
    isLoading,
    isError,
    error: error as Error | null,
    markRead: markReadMutation.mutate,
  };
}
